import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { api } from '../lib/auth';
import { ModalShell } from '../lib/ui';
import { isSplitSlot, halfLabel } from '../lib/trayUtils';

const ipcRenderer = window.process?.type === 'renderer' ? window.require('electron').ipcRenderer : null;

const LAYOUT_URL = 'https://teardrophydro.co.uk/garageFarm/bench-layout';

function authHeaders() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Split slots (half-size micro trays) hold two independent halves, each a
// separate selectable tray - see trayUtils.isSplitSlot.
function extractTrays(layout) {
  if (!layout?.benches) return [];
  const out = [];
  Object.entries(layout.benches).forEach(([benchName, bench]) => {
    bench.layers.forEach((layer, layerIndex) => {
      layer.forEach((slot, trayIndex) => {
        if (isSplitSlot(slot)) {
          (slot.halves || []).forEach((tray, half) => {
            if (tray?.cropType) out.push({ benchName, layerIndex, trayIndex, half, tray });
          });
        } else if (slot?.cropType) {
          out.push({ benchName, layerIndex, trayIndex, tray: slot });
        }
      });
    });
  });
  return out;
}

function cropMatches(tray, product) {
  const a = (tray.cropType || '').toLowerCase().trim();
  const b = (product || '').toLowerCase().trim();
  return b && (a.includes(b) || b.includes(a));
}

function computeUseBy(daysFromNow) {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const newId = () => Math.random().toString(36).slice(2);

const newRow = () => ({
  id:           newId(),
  kind:         'product',
  product:      '',
  weight:       '',
  useBy:        computeUseBy(7),
  storage:      'Keep refrigerated',
  batchId:      '',
  allergen:     'alongside',
  qty:          1,
  preview:      null,
  previewing:   false,
  selectedTray: null,
  pickerOpen:   false,
  traySearch:   '',
});

// Name/address label for a bag that isn't an order, e.g. a sample drop. Same
// layout as the customer label PrintOrderModal puts on order bags.
const newNameRow = () => ({
  id:         newId(),
  kind:       'name',
  name:       '',
  line1:      '',
  city:       '',
  postcode:   '',
  note:       '',
  qty:        1,
  preview:    null,
  previewing: false,
  pickerOpen: false,
  leadSearch: '',
});

// Untouched rows (like the empty one the modal opens with) are left out of
// the print instead of blocking it.
const isBlank = r => r.kind === 'name'
  ? ![r.name, r.line1, r.city, r.postcode, r.note].some(v => v.trim())
  : ![r.product, r.weight, r.batchId].some(v => v.trim());

const labelData = r => r.kind === 'name'
  ? {
      kind:    'address',
      name:    r.name.trim() || 'Name',
      address: { line1: r.line1.trim(), city: r.city.trim(), postcode: r.postcode.trim() },
      note:    r.note.trim(),
    }
  : {
      company:  'Teardrop',
      product:  r.product.trim() || 'Product',
      weight:   r.weight.trim(),
      useBy:    r.useBy,
      batchId:  r.batchId.trim() || null,
      allergen: r.allergen,
      storage:  r.storage,
    };

// Sample drops mostly go to outreach leads, so a lead can fill a name label.
// Leads only have a town/area, not a street address.
const fromLead = lead => ({
  name:       lead.businessName || '',
  city:       lead.location || '',
  note:       lead.contactName ? `FAO ${lead.contactName}` : '',
  pickerOpen: false,
  leadSearch: '',
});

const inp = {
  padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
  fontSize: 13, color: 'var(--text)', background: '#fff', boxSizing: 'border-box',
};

const searchInp = {
  width: '100%', padding: '5px 8px', fontSize: 12,
  border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
  outline: 'none', boxSizing: 'border-box', color: 'var(--text)',
};

const pickerBtn = open => ({
  padding: '4px 7px', fontSize: 13, flexShrink: 0,
  border: `1px solid ${open ? '#86efac' : 'var(--border)'}`,
  borderRadius: 'var(--r-lg)',
  background: open ? '#f0fdf4' : 'var(--panel-2)',
  cursor: 'pointer', color: 'var(--muted)',
});

const colHead = {
  display: 'grid', gap: 6, padding: '0 0 6px', borderBottom: '1px solid var(--border)',
  fontSize: 11, fontWeight: 700, color: 'var(--muted)',
  textTransform: 'uppercase', letterSpacing: '0.04em',
};

const sectionTitle = { margin: '4px 0 8px', fontSize: 13, fontWeight: 700, color: 'var(--text)' };

const addBtn = {
  flex: 1, padding: '7px 14px', fontSize: 13, fontWeight: 600,
  border: '1px dashed var(--border)', borderRadius: 'var(--r-md)',
  background: 'var(--panel-2)', cursor: 'pointer', color: 'var(--muted)',
};

const COLS = '1fr 60px 100px 100px 130px 120px 44px 46px 32px 32px';
const NAME_COLS = '1.4fr 1.5fr 1fr 90px 1.2fr 44px 46px 32px 32px';

export default function CustomLabelModal({ onClose }) {
  const [rows,        setRows]        = useState([newRow()]);
  const [allTrays,    setAllTrays]    = useState([]);
  const [printing,    setPrinting]    = useState(false);
  const [done,        setDone]        = useState(false);
  const [status,      setStatus]      = useState('');
  const [error,       setError]       = useState('');
  const [leads,       setLeads]       = useState(null);  // null until loaded
  const [leadsError,  setLeadsError]  = useState('');
  const leadsRequested = useRef(false);

  useEffect(() => {
    axios.get(LAYOUT_URL, { headers: authHeaders() })
      .then(res => setAllTrays(extractTrays(res.data)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!ipcRenderer) return;
    const handler = (_, msg) => setStatus(msg);
    ipcRenderer.on('print-progress', handler);
    return () => ipcRenderer.removeListener('print-progress', handler);
  }, []);

  const update = (id, fields) =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...fields } : r));

  const remove = id => setRows(prev => prev.filter(r => r.id !== id));

  const duplicate = row => {
    const idx = rows.findIndex(r => r.id === row.id);
    const copy = { ...row, id: newId(), pickerOpen: false, preview: null, previewing: false };
    setRows(prev => [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)]);
  };

  // Leads load the first time a lead picker opens, not every time labels are printed
  const toggleLeadPicker = row => {
    update(row.id, { pickerOpen: !row.pickerOpen, leadSearch: '' });
    if (leadsRequested.current) return;
    leadsRequested.current = true;
    api.get('/leads')
      .then(res => setLeads(res.data || []))
      .catch(() => setLeadsError("Couldn't load outreach leads."));
  };

  const selectTray = (rowId, t) => {
    update(rowId, {
      selectedTray: t,
      batchId:      t.tray.batchId || '',
      pickerOpen:   false,
      traySearch:   '',
    });
  };

  const clearTray = rowId => update(rowId, { selectedTray: null, batchId: '', pickerOpen: false });

  const handlePreview = async (row) => {
    if (!ipcRenderer) return;
    update(row.id, { previewing: true, preview: null });
    const result = await ipcRenderer.invoke('preview-label', labelData(row));
    update(row.id, { previewing: false, preview: result.success ? result.dataUrl : null });
  };

  const nameRows    = rows.filter(r => r.kind === 'name');
  const productRows = rows.filter(r => r.kind !== 'name');
  const printable   = rows.filter(r => !isBlank(r));
  const totalLabels = printable.reduce((n, r) => n + (r.qty || 0), 0);

  const handlePrint = async () => {
    if (printable.some(r => r.kind === 'name' && !r.name.trim())) { setError('Every name label needs a name.'); return; }
    if (printable.some(r => r.kind !== 'name' && !r.product.trim())) { setError('Every product label needs a product name.'); return; }
    if (!ipcRenderer) { setError('Label printing is only available in the desktop app.'); return; }

    setError(''); setPrinting(true); setDone(false);

    // Name labels come out first, like the customer label on an order
    const labelsData = [
      ...printable.filter(r => r.kind === 'name'),
      ...printable.filter(r => r.kind !== 'name'),
    ].flatMap(r => Array.from({ length: r.qty }, () => labelData(r)));

    const result = await ipcRenderer.invoke('print-labels', labelsData);
    setPrinting(false);
    if (result.success) setDone(true);
    else setError(result.error || 'Unknown error');
  };

  // Qty, preview, duplicate and remove: the last four columns of both kinds of row
  const rowTail = row => (
    <>
      <input
        type="number" min={1} max={99}
        style={{ ...inp, width: '100%', textAlign: 'center' }}
        value={row.qty}
        onChange={e => update(row.id, { qty: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })}
      />

      <button
        onClick={() => handlePreview(row)}
        disabled={row.previewing}
        title="Preview label"
        style={{
          padding: '4px 6px', fontSize: 12,
          border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
          background: 'var(--panel-2)', cursor: 'pointer', color: 'var(--muted)',
        }}
      >{row.previewing ? '…' : '👁'}</button>

      <button
        onClick={() => duplicate(row)}
        title="Duplicate row"
        style={{
          padding: '4px 6px', fontSize: 13,
          border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
          background: 'var(--panel-2)', cursor: 'pointer', color: 'var(--muted)',
        }}
      >⧉</button>

      <button
        onClick={() => remove(row.id)}
        disabled={rows.length === 1}
        title="Remove row"
        style={{
          padding: '4px 6px', fontSize: 14, border: 'none',
          borderRadius: 'var(--r-lg)', background: 'none',
          cursor: rows.length === 1 ? 'default' : 'pointer',
          color: rows.length === 1 ? 'var(--border)' : '#e24a4a',
        }}
      >✕</button>
    </>
  );

  return (
    <ModalShell
      onClose={onClose} width={920} maxWidth="97vw" ariaLabel="Custom Labels"
      panelStyle={{ maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
    >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--text)' }}>Custom Labels</h2>
            <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              One row per design - set quantity per row
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'var(--panel-2)', border: 'none', borderRadius: 'var(--r-md)',
            width: 28, height: 28, cursor: 'pointer', fontSize: '14px', color: 'var(--muted)',
          }}>✕</button>
        </div>

        {/* Rows */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {nameRows.length > 0 && (
            <>
              <div style={sectionTitle}>
                Name &amp; address labels
                <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--muted)', marginLeft: 8 }}>
                  Print first, before the product labels
                </span>
              </div>
              <div style={{ ...colHead, gridTemplateColumns: NAME_COLS }}>
                <span>Name</span>
                <span>Address</span>
                <span>Town</span>
                <span>Postcode</span>
                <span>Note</span>
                <span style={{ textAlign: 'center' }}>Qty</span>
                <span></span>
                <span></span>
                <span></span>
              </div>
              <div style={{ paddingTop: 8, marginBottom: 16 }}>
                {nameRows.map((row, i) => {
                  const q = row.leadSearch.toLowerCase().trim();
                  const filteredLeads = (leads || []).filter(l => !q ||
                    [l.businessName, l.contactName, l.location].some(v => (v || '').toLowerCase().includes(q))
                  );

                  return (
                    <div key={row.id} style={{ marginBottom: 4, borderBottom: i < nameRows.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{
                        display: 'grid', gridTemplateColumns: NAME_COLS,
                        gap: 6, alignItems: 'center', padding: '8px 0',
                      }}>
                        {/* Name + lead picker trigger */}
                        <div style={{ display: 'flex', gap: 4, minWidth: 0 }}>
                          <input
                            style={{ ...inp, flex: 1, minWidth: 0 }}
                            placeholder="e.g. The Plough"
                            aria-label="Name"
                            value={row.name}
                            onChange={e => update(row.id, { name: e.target.value })}
                          />
                          <button
                            onClick={() => toggleLeadPicker(row)}
                            title="Fill from outreach leads"
                            style={pickerBtn(row.pickerOpen)}
                          >📇</button>
                        </div>
                        <input
                          style={{ ...inp, width: '100%' }}
                          placeholder="optional"
                          aria-label="Address"
                          value={row.line1}
                          onChange={e => update(row.id, { line1: e.target.value })}
                        />
                        <input
                          style={{ ...inp, width: '100%' }}
                          placeholder="optional"
                          aria-label="Town"
                          value={row.city}
                          onChange={e => update(row.id, { city: e.target.value })}
                        />
                        <input
                          style={{ ...inp, width: '100%' }}
                          placeholder="optional"
                          aria-label="Postcode"
                          value={row.postcode}
                          onChange={e => update(row.id, { postcode: e.target.value })}
                        />
                        <input
                          style={{ ...inp, width: '100%' }}
                          placeholder="e.g. Free sample"
                          aria-label="Note"
                          value={row.note}
                          onChange={e => update(row.id, { note: e.target.value })}
                        />
                        {rowTail(row)}
                      </div>

                      {/* Lead picker dropdown */}
                      {row.pickerOpen && (
                        <div style={{ marginBottom: 8, border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', background: '#fff' }}>
                          <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
                            <input
                              autoFocus
                              type="text"
                              placeholder="Search leads by business, contact or area…"
                              value={row.leadSearch}
                              onChange={e => update(row.id, { leadSearch: e.target.value })}
                              style={searchInp}
                            />
                          </div>
                          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                            {filteredLeads.length === 0 ? (
                              <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>
                                {leadsError || (leads === null ? 'Loading leads…' : q ? `No leads matching "${row.leadSearch}"` : 'No outreach leads yet')}
                              </div>
                            ) : (
                              filteredLeads.map((l, li) => (
                                <div
                                  key={l._id}
                                  onClick={() => update(row.id, fromLead(l))}
                                  style={{
                                    padding: '8px 12px', cursor: 'pointer',
                                    borderBottom: li < filteredLeads.length - 1 ? '1px solid var(--border)' : 'none',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                                  onMouseLeave={e => e.currentTarget.style.background = ''}
                                >
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text)' }}>{l.businessName}</div>
                                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                                      {[l.contactName, l.location].filter(Boolean).join(' · ') || 'No contact or area saved'}
                                    </div>
                                  </div>
                                  <span style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{l.status}</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}

                      {/* Label preview, at roughly printed size */}
                      {row.preview && (
                        <img
                          src={row.preview}
                          alt="Name label preview"
                          style={{
                            width: 320, maxWidth: '100%', display: 'block', marginBottom: 8,
                            border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                            imageRendering: 'pixelated', background: '#fff',
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {productRows.length > 0 && (
            <>
              {nameRows.length > 0 && <div style={sectionTitle}>Product labels</div>}
              <div style={{ ...colHead, gridTemplateColumns: COLS }}>
                <span>Product</span>
                <span>Weight</span>
                <span>Best before</span>
                <span>Batch ID</span>
                <span>Storage</span>
                <span>Allergen</span>
                <span style={{ textAlign: 'center' }}>Qty</span>
                <span></span>
                <span></span>
                <span></span>
              </div>
              <div style={{ paddingTop: 8 }}>
                {productRows.map((row, i) => {
                  const q = row.traySearch.toLowerCase().trim();
                  const matchingTrays = allTrays.filter(t => cropMatches(t, row.product));
                  const filteredTrays = q
                    ? allTrays.filter(t =>
                        (t.tray.cropType || '').toLowerCase().includes(q) ||
                        (t.benchName     || '').toLowerCase().includes(q) ||
                        (t.tray.batchId  || '').toLowerCase().includes(q)
                      )
                    : matchingTrays;

                  return (
                    <div key={row.id} style={{ marginBottom: 4, borderBottom: i < productRows.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      {/* Main row */}
                      <div style={{
                        display: 'grid', gridTemplateColumns: COLS,
                        gap: 6, alignItems: 'center', padding: '8px 0',
                      }}>
                        <input
                          style={{ ...inp, width: '100%' }}
                          placeholder="e.g. Coriander"
                          value={row.product}
                          onChange={e => update(row.id, { product: e.target.value })}
                        />
                        <input
                          style={{ ...inp, width: '100%' }}
                          placeholder="50g"
                          value={row.weight}
                          onChange={e => update(row.id, { weight: e.target.value })}
                        />
                        <input
                          style={{ ...inp, width: '100%' }}
                          value={row.useBy}
                          onChange={e => update(row.id, { useBy: e.target.value })}
                        />

                        {/* Batch ID + tray picker trigger */}
                        <div style={{ display: 'flex', gap: 4, minWidth: 0 }}>
                          {row.selectedTray ? (
                            <div style={{
                              flex: 1, display: 'flex', alignItems: 'center', gap: 6,
                              background: '#f0fdf4', border: '1px solid #86efac',
                              borderRadius: 'var(--r-lg)', padding: '3px 8px', minWidth: 0,
                            }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {row.selectedTray.tray.batchId || row.selectedTray.tray.cropType}
                                </div>
                                <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                                  {row.selectedTray.benchName} · L{row.selectedTray.layerIndex + 1} T{row.selectedTray.trayIndex + 1}{row.selectedTray.half !== undefined ? halfLabel(row.selectedTray.half) : ''}
                                </div>
                              </div>
                              <button
                                onClick={() => clearTray(row.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 12, padding: 0, lineHeight: 1 }}
                              >✕</button>
                            </div>
                          ) : (
                            <input
                              style={{ ...inp, flex: 1, minWidth: 0 }}
                              placeholder="optional"
                              value={row.batchId}
                              onChange={e => update(row.id, { batchId: e.target.value })}
                            />
                          )}
                          <button
                            onClick={() => update(row.id, { pickerOpen: !row.pickerOpen, traySearch: '' })}
                            title="Pick from farm"
                            style={pickerBtn(row.pickerOpen)}
                          >🌿</button>
                        </div>

                        {/* Storage */}
                        <input
                          style={{ ...inp, width: '100%' }}
                          value={row.storage}
                          onChange={e => update(row.id, { storage: e.target.value })}
                        />

                        {/* Allergen */}
                        <div style={{ display: 'flex', gap: 3 }}>
                          {['alongside', 'contains'].map(opt => (
                            <label key={opt} style={{
                              flex: 1, textAlign: 'center', fontSize: 11, cursor: 'pointer',
                              padding: '4px 2px', borderRadius: 'var(--r-sm)',
                              background: row.allergen === opt ? 'rgba(46,125,50,0.12)' : 'var(--panel-2)',
                              border: `1px solid ${row.allergen === opt ? 'var(--brand-2)' : 'var(--border)'}`,
                              color: row.allergen === opt ? 'var(--brand)' : 'var(--muted)',
                              fontWeight: row.allergen === opt ? 700 : 400,
                            }}>
                              <input
                                type="radio" name={`allergen-${row.id}`} value={opt}
                                checked={row.allergen === opt}
                                onChange={() => update(row.id, { allergen: opt })}
                                style={{ display: 'none' }}
                              />
                              {opt === 'alongside' ? 'Alongside' : 'Contains'}
                            </label>
                          ))}
                        </div>

                        {rowTail(row)}
                      </div>

                      {/* Tray picker dropdown */}
                      {row.pickerOpen && (
                        <div style={{ marginBottom: 8, border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', background: '#fff' }}>
                          <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
                            <input
                              autoFocus
                              type="text"
                              placeholder="Search by crop, bench or batch ID…"
                              value={row.traySearch}
                              onChange={e => update(row.id, { traySearch: e.target.value })}
                              style={searchInp}
                            />
                          </div>
                          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                            {filteredTrays.length === 0 ? (
                              <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>
                                {q ? `No trays matching "${row.traySearch}"` : 'No auto-matching trays - type to search all'}
                              </div>
                            ) : (
                              filteredTrays.map((t, ti) => (
                                <div
                                  key={`${t.benchName}-${t.layerIndex}-${t.trayIndex}-${t.half ?? 'f'}`}
                                  onClick={() => selectTray(row.id, t)}
                                  style={{
                                    padding: '8px 12px', cursor: 'pointer',
                                    borderBottom: ti < filteredTrays.length - 1 ? '1px solid var(--border)' : 'none',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                                  onMouseLeave={e => e.currentTarget.style.background = ''}
                                >
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text)' }}>
                                      {t.tray.cropType}
                                      {!q && matchingTrays.includes(t) && (
                                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#16a34a' }}>✓ match</span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                                      {t.benchName} · L{t.layerIndex + 1} T{t.trayIndex + 1}{t.half !== undefined ? halfLabel(t.half) : ''}
                                      {t.tray.seededAt && ` · Seeded ${t.tray.seededAt}`}
                                    </div>
                                  </div>
                                  {t.tray.batchId ? (
                                    <span style={{
                                      fontSize: 11, fontWeight: 700, color: 'var(--brand-2)',
                                      background: 'var(--panel-2)', border: '1px solid var(--border)',
                                      borderRadius: 'var(--r-sm)', padding: '2px 7px', whiteSpace: 'nowrap',
                                    }}>{t.tray.batchId}</span>
                                  ) : (
                                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>no batch ID</span>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}

                      {/* Label preview */}
                      {row.preview && (
                        <div style={{ marginBottom: 8 }}>
                          <img
                            src={row.preview}
                            alt="Label preview"
                            style={{
                              width: '100%', display: 'block',
                              border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                              imageRendering: 'pixelated', background: '#fff',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button onClick={() => setRows(prev => [...prev, newRow()])} style={addBtn}>+ Product label</button>
            <button onClick={() => setRows(prev => [...prev, newNameRow()])} style={addBtn}>+ Name / address label</button>
          </div>
        </div>

        {/* Status / error */}
        {status && (
          <div style={{
            background: done ? '#f0fdf4' : '#f0f9ff',
            border: `1px solid ${done ? '#86efac' : '#bae6fd'}`,
            color: done ? '#166534' : '#0369a1',
            borderRadius: 'var(--r-lg)', padding: '8px 12px', fontSize: 12, marginTop: 12,
          }}>{status}</div>
        )}
        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626',
            borderRadius: 'var(--r-lg)', padding: '8px 12px', fontSize: 12, marginTop: 12,
          }}>{error}</div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          <button
            onClick={onClose}
            style={{
              background: 'var(--panel-2)', border: 'none', borderRadius: 'var(--r-md)',
              padding: '8px 18px', fontSize: 13, cursor: 'pointer',
              color: 'var(--muted)', fontWeight: 500,
            }}
          >{done ? 'Close' : 'Cancel'}</button>
          {!done && (
            <button
              onClick={handlePrint}
              disabled={printing || totalLabels === 0}
              style={{
                background: printing ? 'var(--muted)' : 'var(--brand-2)',
                color: '#fff', border: 'none', borderRadius: 'var(--r-md)',
                padding: '8px 22px', fontSize: 13,
                cursor: printing ? 'default' : 'pointer', fontWeight: 600,
              }}
            >
              {printing ? 'Printing…' : `Print ${totalLabels} label${totalLabels !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
    </ModalShell>
  );
}
